import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import {
  getFieldDate,
  getFieldFormattedValue,
  getFieldName,
  getFieldText,
} from "../scripts/helperScripts";
import DivisionsSelect from "./common/DivisionsSelect";
import FieldDisplay from "./common/FieldDisplay";
import SearchBox from "./common/SearchBox";
import ToggleSwitch from "./common/ToggleSwitch";
import {
  getDivisionActiveFosterHomes,
  getDivisionFosterHomes,
} from "./services/soar";

function FosterHomes() {
  const [homes, setHomes] = useState([]);
  const [statusFilter, setStatusFilter] = useState("Licensed");
  const [addresses, setAddresses] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDivision, setSelectedDivision] = useState("");
  const isMountedRef = useRef(true);
  const navigate = useNavigate();
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  const [divisions, setDivisions] = useState([]);
  const [modules, setModules] = useState([]);

  const divisionOptions = useMemo(() => {
    const dOptions = [
      ...divisions.map((d) => ({
        name: d,
        value: d,
      })),
    ];

    if (modules.includes("Admin")) {
      dOptions.unshift({ name: "All", value: "All" });
      dOptions.unshift({ name: "Select a division...", value: "" });
    } else {
      dOptions.unshift({ name: "All", value: "All" });
    }

    return dOptions;
  }, [divisions, modules]);

  //Route Access Protection
  useEffect(() => {
    if (safyInfo?.user?.modules) {
      const userModules = safyInfo.user.modules;

      if (userModules.includes("Foster Homes") === false) {
        navigate("/");
      }

      console.log("useEffect fired");
      if (!userModules.includes("Admin") && !selectedDivision) {
        setSelectedDivision("All");
      }

      setModules((m) => (m.length !== userModules.length ? userModules : m));
      setDivisions((d) =>
        d.length !== safyInfo.user.divisions.length
          ? safyInfo.user.divisions
          : d
      );
    } else navigate("/");
  }, [
    safyInfo?.user?.modules,
    safyInfo?.user?.divisions,
    navigate,
    selectedDivision,
  ]);

  useEffect(() => {
    document.title = "SAFY Portal - Foster Homes";
    return () => {
      document.title = "SAFY Portal";
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    let toastId;

    if (divisions.length > 0) {
      toastId = toast.loading("Loading Foster Homes...", {
        type: toast.TYPE.INFO,
      });
      const signal = controller.signal;
      Promise.all([
        getDivisionFosterHomes(divisions),
        getDivisionActiveFosterHomes(divisions),
      ])
        .then((results) => {
          if (!signal.aborted && results) {
            setAddresses(results[0]);
            setHomes(results[1]);
            toast.update(toastId, {
              render: "Foster Homes loaded successfully.",
              type: toast.TYPE.SUCCESS,
              isLoading: false,
              autoClose: 5000,
              closeOnClick: true,
            });
          } else toast.dismiss(toastId);
        })
        .catch(() => {
          toast.update(toastId, {
            render: "An error occurred while loading Foster Homes.",
            type: toast.TYPE.ERROR,
            isLoading: false,
            autoClose: 5000,
            closeOnClick: true,
          });
        });
    }

    return () => {
      controller.abort();
      toastId && toast.dismiss(toastId);
    };
  }, [divisions]);

  const filteredHomes = useMemo(() => {
    let filtered = homes;

    if (statusFilter === "Licensed") {
      if (searchQuery !== "") {
        if (selectedDivision !== "All") {
          filtered = filtered.filter((home) => {
            if (!home.ccx_unitdetail) {
              return false;
            }

            const unitDetails = getFieldName(home.ccx_unitdetail).toLowerCase();

            if (
              home.svc_licenseddate !== undefined &&
              home.ccx_closeddate === undefined &&
              !unitDetails.includes("non safy") &&
              !unitDetails.includes("non-safy")
            ) {
              if (
                home.ccx_name
                  .toLowerCase()
                  .includes(searchQuery.toLowerCase()) ||
                home.sor_fosterhomeid
                  .toLowerCase()
                  .includes(searchQuery.toLowerCase())
              )
                if (
                  getFieldName(home.ccx_programunit)
                    .toLowerCase()
                    .startsWith(selectedDivision.toLowerCase())
                )
                  return true;
            }
            return false;
          });
        } else {
          filtered = filtered.filter((home) => {
            if (!home.ccx_unitdetail) {
              return false;
            }

            const unitDetails = getFieldName(home.ccx_unitdetail).toLowerCase();

            if (
              home.svc_licenseddate !== undefined &&
              home.ccx_closeddate === undefined &&
              !unitDetails.includes("non safy") &&
              !unitDetails.includes("non-safy")
            )
              if (
                home.ccx_name
                  .toLowerCase()
                  .includes(searchQuery.toLowerCase()) ||
                home.sor_fosterhomeid
                  .toLowerCase()
                  .includes(searchQuery.toLowerCase())
              )
                return true;
            return false;
          });
        }
      } else if (selectedDivision !== "All") {
        filtered = filtered.filter((home) => {
          if (!home.ccx_unitdetail) {
            return false;
          }

          const unitDetails = getFieldName(home.ccx_unitdetail).toLowerCase();

          if (
            home.svc_licenseddate !== undefined &&
            home.ccx_closeddate === undefined &&
            !unitDetails.includes("non safy") &&
            !unitDetails.includes("non-safy")
          )
            if (
              getFieldName(home.ccx_programunit)
                .toLowerCase()
                .startsWith(selectedDivision.toLowerCase())
            )
              return true;
          return false;
        });
      } else {
        filtered = filtered.filter((home) => {
          if (!home.ccx_unitdetail) {
            return false;
          }

          const unitDetails = getFieldName(home.ccx_unitdetail).toLowerCase();

          return (
            home.svc_licenseddate !== undefined &&
            home.ccx_closeddate === undefined &&
            home.ccx_unitdetail &&
            !unitDetails.includes("non safy") &&
            !unitDetails.includes("non-safy")
          );
        });
      }
    } else if (statusFilter === "Unlicensed") {
      if (searchQuery !== "") {
        if (selectedDivision !== "All") {
          filtered = filtered.filter((home) => {
            if (!home.ccx_unitdetail) {
              return false;
            }

            const unitDetails = getFieldName(home.ccx_unitdetail).toLowerCase();

            if (
              (home.svc_licenseddate === undefined ||
                unitDetails.includes("non safy") ||
                unitDetails.includes("non-safy")) &&
              home.ccx_closeddate === undefined
            ) {
              if (
                home.ccx_name
                  .toLowerCase()
                  .includes(searchQuery.toLowerCase()) ||
                home.sor_fosterhomeid
                  .toLowerCase()
                  .includes(searchQuery.toLowerCase())
              ) {
                if (
                  getFieldName(home.ccx_programunit)
                    .toLowerCase()
                    .startsWith(selectedDivision.toLowerCase())
                ) {
                  return true;
                }
              }
            }

            return false;
          });
        } else {
          filtered = filtered.filter((home) => {
            if (!home.ccx_unitdetail) {
              return false;
            }

            const unitDetails = getFieldName(home.ccx_unitdetail).toLowerCase();

            if (
              (home.svc_licenseddate === undefined ||
                unitDetails.includes("non safy") ||
                unitDetails.includes("non-safy")) &&
              home.ccx_closeddate === undefined
            ) {
              if (
                home.ccx_name
                  .toLowerCase()
                  .includes(searchQuery.toLowerCase()) ||
                home.sor_fosterhomeid
                  .toLowerCase()
                  .includes(searchQuery.toLowerCase())
              ) {
                return true;
              }
            }
            return false;
          });
        }
      } else if (selectedDivision !== "All") {
        filtered = filtered.filter((home) => {
          if (!home.ccx_unitdetail) {
            return false;
          }

          const unitDetails = getFieldName(home.ccx_unitdetail).toLowerCase();

          if (
            (home.svc_licenseddate === undefined ||
              unitDetails.includes("non safy") ||
              unitDetails.includes("non-safy")) &&
            home.ccx_closeddate === undefined
          ) {
            if (
              getFieldName(home.ccx_programunit)
                .toLowerCase()
                .startsWith(selectedDivision.toLowerCase())
            ) {
              return true;
            }
          }
          return false;
        });
      } else {
        filtered = filtered.filter((home) => {
          if (!home.ccx_unitdetail) {
            return false;
          }

          const unitDetails = getFieldName(home.ccx_unitdetail).toLowerCase();

          if (
            (home.svc_licenseddate === undefined ||
              unitDetails.includes("non safy") ||
              unitDetails.includes("non-safy")) &&
            home.ccx_closeddate === undefined
          ) {
            return true;
          }

          return false;
        });
      }
    } else if (statusFilter === "Closed") {
      if (searchQuery !== "") {
        if (selectedDivision !== "All") {
          filtered = filtered.filter((home) => {
            if (!home.ccx_unitdetail) {
              return false;
            }

            if (home.ccx_closeddate !== undefined)
              if (
                home.ccx_name
                  .toLowerCase()
                  .includes(searchQuery.toLowerCase()) ||
                home.sor_fosterhomeid
                  .toLowerCase()
                  .includes(searchQuery.toLowerCase())
              )
                if (
                  getFieldName(home.ccx_programunit)
                    .toLowerCase()
                    .startsWith(selectedDivision.toLowerCase())
                )
                  return true;
            return false;
          });
        } else {
          filtered = filtered.filter((home) => {
            if (!home.ccx_unitdetail) {
              return false;
            }

            if (home.ccx_closeddate !== undefined)
              if (
                home.ccx_name
                  .toLowerCase()
                  .includes(searchQuery.toLowerCase()) ||
                home.sor_fosterhomeid
                  .toLowerCase()
                  .includes(searchQuery.toLowerCase())
              )
                return true;
            return false;
          });
        }
      } else if (selectedDivision && selectedDivision !== "All") {
        filtered = filtered.filter((home) => {
          if (!home.ccx_unitdetail) {
            return false;
          }

          if (home.ccx_closeddate !== undefined)
            if (
              getFieldName(home.ccx_programunit)
                .toLowerCase()
                .startsWith(selectedDivision.toLowerCase())
            )
              return true;
          return false;
        });
      } else {
        filtered = filtered.filter(
          (home) => home.ccx_unitdetail && home.ccx_closeddate !== undefined
        );
      }
    }

    return filtered.sort((a, b) => a.ccx_name.localeCompare(b.ccx_name));
  }, [homes, statusFilter, searchQuery, selectedDivision]);

  const handleSearch = ({ currentTarget: input }) => {
    setSearchQuery(input.value);
  };

  const handleDivisionSelect = (division) => {
    setSelectedDivision(division);
  };

  const handleToggleChange = (value) => {
    setStatusFilter(value);
  };

  const openNewFPInquiry = () => {
    const url = `${process.env.REACT_APP_ORG}/main.aspx?etc=10231&pagetype=entityrecord`;
    window.open(url);
  };

  const openActiveFPInquiries = () => {
    const url = `${process.env.REACT_APP_ORG}/main.aspx?etc=10231&pagetype=entitylist&viewid=%7b02859D4A-8954-E311-BB4A-005056822045%7d&viewtype=1039`;
    window.open(url);
  };

  const openNewFosterHome = () => {
    const url = `${process.env.REACT_APP_ORG}/main.aspx?etc=10221&pagetype=entityrecord`;
    window.open(url);
  };

  return (
    <div>
      <div className="container">
        <div className="row">
          <div className="col">
            <h1>Foster Homes</h1>
          </div>
          <div className="col">
            <button className="btn btn-primary me-2" onClick={openNewFPInquiry}>
              New FP Inquiry
            </button>
            <button
              className="btn btn-primary me-2"
              onClick={openActiveFPInquiries}
            >
              Open Active FP Inquiries
            </button>
            <button className="btn btn-primary" onClick={openNewFosterHome}>
              New Foster Home
            </button>
          </div>
        </div>
      </div>
      <div className="row row-cols-1 row-cols-sm-2 row-cols-md-2 row-cols-lg-2 row-cols-xl-2 row-cols-xxl-2">
        <div className="col">
          <DivisionsSelect
            options={divisionOptions}
            onChange={handleDivisionSelect}
          />
        </div>
        <div className="col">
          <SearchBox
            value={searchQuery}
            label="Search"
            onChange={handleSearch}
            clearSearch={() => setSearchQuery("")}
            placeholder="Search Foster Home Name"
          />
        </div>
      </div>
      <div className="row mt-2">
        <div className="col">
          <div className="clearfix">
            <div className="float-start">
              Foster Home count:{" "}
              <span className="bg-success text-white px-2 py-1 rounded">
                <b>{filteredHomes.length}</b>
              </span>
            </div>
            <div className="float-end">
              <ToggleSwitch
                values={["Licensed", "Unlicensed", "Closed"]}
                selected="Licensed"
                onChange={handleToggleChange}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="row row-cols-1 row-cols-sm-1 row-cols-md-1 row-cols-lg-2 row-cols-xl-2">
        {filteredHomes.map((home) => (
          <div className="p-2" key={home.ccx_fostercarehomeid}>
            <div className="card card-safy" style={{ height: "100%" }}>
              <div className="card-header bg-safy">
                <div className="row pe-2">
                  <div className="col-10">
                    <div className="h4 mt-0 mb-0 fw-light">
                      {home.sor_fosterhomeid
                        ? home.sor_fosterhomeid + "-" + home.ccx_name
                        : home.ccx_name}
                    </div>
                  </div>
                  <div className="col-2 p-0 d-flex justify-content-end">
                    {process.env.REACT_APP_ENVIRONMENT === "Production" ? (
                      <i
                        className="fas fa-folder fa-2x me-2"
                        title="Documents"
                        style={{ color: "#F0AD4E" }}
                        onClick={() =>
                          navigate(
                            `/fosterhomes/${home.ccx_fostercarehomeid.replace(
                              /[{}]/g,
                              ""
                            )}`,
                            {
                              state: {
                                home: home,
                                tab: "documents",
                                addresses: addresses.filter(
                                  (address) =>
                                    getFieldText(address.ccx_parentid) ===
                                    home.ccx_fostercarehomeid
                                ),
                              },
                            }
                          )
                        }
                      ></i>
                    ) : null}
                    <Link
                      title="Open Foster Home Record"
                      className="btn btn-success h-100"
                      to={`/fosterhomes/${home.ccx_fostercarehomeid.replace(
                        /[{}]/g,
                        ""
                      )}`}
                      state={{
                        home: home,
                        addresses: addresses.filter(
                          (address) =>
                            getFieldText(address.ccx_parentid) ===
                            home.ccx_fostercarehomeid
                        ),
                      }}
                    >
                      <i
                        className="far fa-address-card fa-lg "
                        style={{ color: "#FFFFFF" }}
                      ></i>
                    </Link>
                  </div>
                </div>
              </div>
              <div
                className={`card-body${
                  statusFilter === "Unlicensed" &&
                  home.svc_licenseddate === undefined
                    ? " bg-warning"
                    : statusFilter === "Closed"
                    ? " bg-warning"
                    : ""
                }`}
              >
                <div className="row row-cols-1 row-cols-sm-2 ">
                  <div className="col">
                    {addresses
                      .filter(
                        (address) =>
                          getFieldText(address.ccx_parentid) ===
                          home.ccx_fostercarehomeid
                      )
                      .map((address) => (
                        <React.Fragment
                          key={address.ccx_fostercarehomeaddressid}
                        >
                          <h6>{address.ccx_line1}</h6>
                          <h6>
                            {address.ccx_city && `${address.ccx_city}, `}
                            {address.ccx_state_lu &&
                              `${getFieldName(address.ccx_state_lu)} `}
                            {address.ccx_postalcode}
                          </h6>
                          {address.ccx_county_lu && (
                            <h6>{`${getFieldName(
                              address.ccx_county_lu
                            )} County`}</h6>
                          )}
                          {address.ccx_telephone1 && (
                            <h6>Phone: {address.ccx_telephone1}</h6>
                          )}
                          {address.ccx_telephone2 && (
                            <h6>Phone 2: {address.ccx_telephone2}</h6>
                          )}
                        </React.Fragment>
                      ))}
                  </div>
                  <div className="col">
                    <FieldDisplay
                      label="Licensed Date"
                      value={getFieldDate(home.svc_licenseddate)}
                    />
                    {home.ccx_closeddate ? (
                      <FieldDisplay
                        label="Closed Date"
                        value={getFieldDate(home.ccx_closeddate)}
                      />
                    ) : null}
                    {home.ccx_expirationdate ? (
                      <FieldDisplay
                        label="Expiration Date"
                        value={getFieldDate(home.ccx_expirationdate)}
                      />
                    ) : null}
                    {home.ccx_capacity !== undefined ? (
                      <FieldDisplay
                        label="Capacity"
                        value={getFieldFormattedValue(home.ccx_capacity)}
                      />
                    ) : null}
                  </div>
                </div>
                {home.ccx_unitdetail && (
                  <div className="h6 mt-2">
                    {getFieldName(home.ccx_unitdetail)}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default FosterHomes;
